import { Peer }  from './peer';
import { Logger } from "../utils/logger";
import { WebSocketSignalingService } from '../signaling/websocket-signaling.service';
import { SignalingService } from '../signaling/signaling.service';

// Definizione di funzioni e classi

export function uuid4(): string {
  const temp_url = URL.createObjectURL(new Blob());
  const uuid = temp_url.toString();
  URL.revokeObjectURL(temp_url);
  return uuid.split(/[:/]/g).pop()?.toLowerCase() || ''; // remove prefixes
}

export class RenderStreaming {
  private _peer: Peer | null = null;
  private _connectionId!: string;
  private _config: RTCConfiguration | string;
  private _signaling!: WebSocketSignalingService | SignalingService; // Sostituire `any` con il tipo corretto per `_signaling`
  isStarted: boolean = false;
  typeOfConnection: string | null = null;

  getPeer(): Peer | null {
    return this._peer;
  }

  onConnect: (connectionId: string) => void = (connectionId) => {
    Logger.log(`Connect peer on ${connectionId}.`);
  };
  onDisconnect: (connectionId: string) => void = (connectionId) => {
    Logger.log(`Disconnect peer on ${connectionId}.`);
  };
  onGotOffer: (connectionId: string) => void = (connectionId) => {
    Logger.log(`On got Offer on ${connectionId}.`);
  };
  onGotAnswer: (connectionId: string) => void = (connectionId) => {
    Logger.log(`On got Answer on ${connectionId}.`);
  };
  onTrackEvent: (data: any) => void = (data) => {
    Logger.log(`OnTrack event peer with data:${data}`);
  };
  onAddChannel: (data: any) => void = (data) => {
    Logger.log(`onAddChannel event peer with data:${data}`);
  };
  onFirstNegotiationEnd: (connectionId: string) => void = (connectionId) => {
    Logger.log(`On first negotiation end ${connectionId}.`);
  };

  constructor(signaling: WebSocketSignalingService | SignalingService, config: RTCConfiguration | string) {  // Modifica `any` con il tipo corretto per `signaling`
    this._config = config;
    this.setSignaling(signaling);
    // this._signaling = signaling;
    // this._signaling.addEventListener('connect', this._onConnect.bind(this));
    // this._signaling.addEventListener('disconnect', this._onDisconnect.bind(this));
    // this._signaling.addEventListener('offer', this._onOffer.bind(this));
    // this._signaling.addEventListener('answer', this._onAnswer.bind(this));
    // this._signaling.addEventListener('candidate', this._onIceCandidate.bind(this));
  }

  public setSignaling(signaling: WebSocketSignalingService | SignalingService) {
    this._signaling = signaling;
    this._signaling.addEventListener('connect', this._onConnect.bind(this));
    this._signaling.addEventListener('disconnect', this._onDisconnect.bind(this));
    this._signaling.addEventListener('offer', this._onOffer.bind(this));
    this._signaling.addEventListener('answer', this._onAnswer.bind(this));
    this._signaling.addEventListener('candidate', this._onIceCandidate.bind(this));
  }

  private async _onConnect(e: Event): Promise<void> {
    const data = (e as CustomEvent).detail;
    if (this._connectionId && this._connectionId === data.connectionId) {
      this._preparePeerConnection(this._connectionId, data.polite);
      this.onConnect(data.connectionId);
    }
  }

  private async _onDisconnect(e: Event): Promise<void> {
    const data = (e as CustomEvent).detail;
    if (this._connectionId === data.connectionId) {
      this.onDisconnect(data.connectionId);
      if (this._peer) {
        this._peer.close();
        this._peer = null;
      }
    }
  }

  private async _onOffer(e: Event): Promise<void> {
    const offer = (e as CustomEvent).detail;
    if (!this._peer) {
      this._preparePeerConnection(offer.connectionId, offer.polite);
    }
    const desc = new RTCSessionDescription({ sdp: offer.sdp, type: "offer" });
    try {
      await this._peer?.onGotDescription(offer.connectionId, desc);
    } catch (error) {
      Logger.warn(`Error happen on GotDescription that description.\n Message: ${error}\n RTCSdpType:${desc.type}\n sdp:${desc.sdp}`);
      return;
    }
  }

  private async _onAnswer(e: Event): Promise<void> {
    const answer = (e as CustomEvent).detail;
    const desc = new RTCSessionDescription({ sdp: answer.sdp, type: "answer" });
    if (this._peer) {
      try {
        await this._peer.onGotDescription(answer.connectionId, desc);
      } catch (error) {
        Logger.warn(`Error happen on GotDescription that description.\n Message: ${error}\n RTCSdpType:${desc.type}\n sdp:${desc.sdp}`);
        return;
      }
    }
  }

  private async _onIceCandidate(e: Event): Promise<void> {
    const candidate = (e as CustomEvent).detail;
    const iceCandidate = new RTCIceCandidate({
      candidate: candidate.candidate,
      sdpMid: candidate.sdpMid,
      sdpMLineIndex: candidate.sdpMLineIndex
    });
    if (this._peer) {
      await this._peer.onGotCandidate(candidate.connectionId, iceCandidate);
    }
  }

  async createConnection(connectionId: string | null = null, typeOfConnection: string | null = null): Promise<void> {
    this._connectionId = connectionId ? connectionId : uuid4();
    this.typeOfConnection = typeOfConnection;
    await this._signaling.createConnection(this._connectionId, this.typeOfConnection);
  }

  async deleteConnection(): Promise<void> {
    await this._signaling.deleteConnection(this._connectionId);
  }

  private _preparePeerConnection(connectionId: string, polite: boolean): Peer {
    if (this._peer) {
      Logger.log('Close current PeerConnection');
      this._peer.close();
      this._peer = null;
    }

    // Crea la connessione peer e imposta i gestori
    console.log("preparePeerConnection", connectionId, polite, this._config);
    this._peer = new Peer(connectionId, polite, this._config);
    this._peer.addEventListener('disconnect', () => {
      this.onDisconnect(`Receive disconnect message from peer. connectionId:${connectionId}`);
    });
    this._peer.addEventListener('trackevent', (e) => {
      const data = (e as CustomEvent).detail;
      this.onTrackEvent(data);
    });
    this._peer.addEventListener('adddatachannel', (e) => {
      const data = (e as CustomEvent).detail;
      this.onAddChannel(data);
    });
    this._peer.addEventListener('ongotoffer', (e) => {
      const id = (e as CustomEvent).detail.connectionId;
      this.onGotOffer(id);
    });
    this._peer.addEventListener('ongotanswer', (e) => {
      const id = (e as CustomEvent).detail.connectionId;
      this.onGotAnswer(id);
    });
    this._peer.addEventListener('sendoffer', (e) => {
      const offer = (e as CustomEvent).detail;
      this._signaling.sendOffer(offer.connectionId, offer.sdp);
    });
    this._peer.addEventListener('sendanswer', (e) => {
      const answer = (e as CustomEvent).detail;
      this._signaling.sendAnswer(answer.connectionId, answer.sdp);
    });
    this._peer.addEventListener('sendcandidate', (e) => {
      const candidate = (e as CustomEvent).detail;
      this._signaling.sendCandidate(candidate.connectionId, candidate.candidate, candidate.sdpMid, candidate.sdpMLineIndex);
    });

    this._peer.addEventListener('firstnegotiationend', (e) => {
      const id = (e as CustomEvent).detail.connectionId;
      this.onFirstNegotiationEnd(id);
    });


    return this._peer;
  }

  async getStats(): Promise<RTCStatsReport | null> {
    return this._peer?.getStats(this._connectionId) || null;
  }

  createDataChannel(label: string): RTCDataChannel | null {
    return this._peer?.createDataChannel(this._connectionId, label) || null;
  }

  addTrack(track: MediaStreamTrack): RTCRtpSender | null {
    return this._peer?.addTrack(this._connectionId, track) || null;
  }

  addStream(stream: MediaStream): RTCRtpSender[] | null {
    const ret: RTCRtpSender[] = [];
    stream.getTracks().forEach((track: MediaStreamTrack) => {
      const ta = this._peer?.addTrack(this._connectionId, track) || null;
      if(ta != null) {
        ret.push(ta);
      }
    })
    if(ret.length == 0)
      return null;
    return ret;
  }

  removeSender(sender: RTCRtpSender): void {
    this._peer?.removeTrack(this._connectionId, sender);
  }


  addTransceiver(trackOrKind: MediaStreamTrack | string, init?: RTCRtpTransceiverInit): RTCRtpTransceiver | null {
    return this._peer?.addTransceiver(this._connectionId, trackOrKind, init) || null;
  }

  getTransceivers(): RTCRtpTransceiver[] | null {
    return this._peer?.getTransceivers(this._connectionId) || null;
  }

  async start(): Promise<void> {
    await this._signaling.start();
    this.isStarted = true;
    this.checkConnection();
  }

  checkConnection(): void {
    if(this._signaling instanceof WebSocketSignalingService) {
      this._signaling.getIsWsOpen().subscribe((isWsOpen) => {
        if(this.isStarted && !isWsOpen) {
          setTimeout(() => {
            this.start();
          }, 1000)
        } else if(!this.isStarted && isWsOpen) {
          this.stop();
        }
      })
      
    }
  }

  async stop(): Promise<void> {
    if (this._peer) {
      this._peer.close();
      this._peer = null;
    }

    if (this._signaling) {
      await this._signaling.stop();
      this.isStarted = false;
      // this._signaling = undefined;
    }
  }
}
